/**
 * Weather Filter Store
 *
 * Zustand store for persisting weather file library filter state.
 * Persists user's filter selections across page refreshes and sessions.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
  ASHRAEClimateZone,
  WeatherFileSourceType,
  WeatherLibraryViewMode,
} from './types';

interface WeatherFilterState {
  // Search and filter state
  searchText: string;
  selectedCountry: string | null;
  selectedClimateZones: ASHRAEClimateZone[];
  selectedSourceTypes: WeatherFileSourceType[];
  selectedYearRanges: string[];

  // View state
  viewMode: WeatherLibraryViewMode;

  // Actions
  setSearchText: (text: string) => void;
  setSelectedCountry: (country: string | null) => void;
  setSelectedClimateZones: (zones: ASHRAEClimateZone[]) => void;
  setSelectedSourceTypes: (types: WeatherFileSourceType[]) => void;
  setSelectedYearRanges: (ranges: string[]) => void;
  setViewMode: (mode: WeatherLibraryViewMode) => void;
  clearAllFilters: () => void;
  hasActiveFilters: () => boolean;
  getActiveFilterCount: () => number;
}

export const useWeatherFilterStore = create<WeatherFilterState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        searchText: '',
        selectedCountry: null,
        selectedClimateZones: [],
        selectedSourceTypes: [],
        selectedYearRanges: [],
        viewMode: 'list',

        // Actions
        setSearchText: (text) => {
          set({ searchText: text });
        },

        setSelectedCountry: (country) => {
          set({ selectedCountry: country });
        },

        setSelectedClimateZones: (zones) => {
          set({ selectedClimateZones: zones });
        },

        setSelectedSourceTypes: (types) => {
          set({ selectedSourceTypes: types });
        },

        setSelectedYearRanges: (ranges) => {
          set({ selectedYearRanges: ranges });
        },

        setViewMode: (mode) => {
          set({ viewMode: mode });
        },

        clearAllFilters: () => {
          set({
            searchText: '',
            selectedCountry: null,
            selectedClimateZones: [],
            selectedSourceTypes: [],
            selectedYearRanges: [],
          });
        },

        hasActiveFilters: () => {
          const state = get();
          return (
            state.searchText.trim() !== '' ||
            state.selectedCountry !== null ||
            state.selectedClimateZones.length > 0 ||
            state.selectedSourceTypes.length > 0 ||
            state.selectedYearRanges.length > 0
          );
        },

        getActiveFilterCount: () => {
          const state = get();
          return (
            (state.selectedCountry ? 1 : 0) +
            state.selectedClimateZones.length +
            state.selectedSourceTypes.length +
            state.selectedYearRanges.length
          );
        },
      }),
      {
        name: 'weather-filter-storage',
        // Only persist filter preferences, not temporary UI state
        partialize: (state) => ({
          searchText: state.searchText,
          selectedCountry: state.selectedCountry,
          selectedClimateZones: state.selectedClimateZones,
          selectedSourceTypes: state.selectedSourceTypes,
          selectedYearRanges: state.selectedYearRanges,
          viewMode: state.viewMode,
        }),
      }
    ),
    { name: 'WeatherFilterStore' }
  )
);

/**
 * Selector hooks for common queries
 */
export const useWeatherViewMode = () => {
  return useWeatherFilterStore((state) => state.viewMode);
};

export const useWeatherFiltersActive = () => {
  return useWeatherFilterStore((state) => state.hasActiveFilters());
};

export const useWeatherFilterCount = () => {
  return useWeatherFilterStore((state) => state.getActiveFilterCount());
};
